import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { DocumentViewer } from "@/components/inbox/DocumentViewer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Paperclip, Search, FileText, CreditCard, Truck, Loader2, Plus, Trash2, Bell, ScrollText, FileSignature, ClipboardList, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { DeliveryCheckBadge, DeliveryCheckPanel } from "./DeliveryCheckPanel";

export const KIND_LABELS = {
  payment_receipt: "Zahlungsbeleg",
  delivery_note: "Lieferschein",
  reminder: "Mahnung",
  terms: "AGB",
  quote: "Offerte",
  contract: "Vertrag",
  other: "Beilage",
};

export const attachmentLabel = (a, bill) => {
  if (a.kind && KIND_LABELS[a.kind]) return KIND_LABELS[a.kind];
  const name = a.file_name || "";
  if (bill?.payment_receipt_file_id === a.file_id || /receipt|quittung|zahlungsbeleg|ricevuta|reçu/i.test(name)) return "Zahlungsbeleg";
  if (/lieferschein|delivery|bolla|livraison/i.test(name)) return "Lieferschein";
  if (/mahnung|reminder|rappel/i.test(name)) return "Mahnung";
  if (/agb|terms|conditions/i.test(name)) return "AGB";
  return "Beilage";
};

const confidenceText = (a) => {
  if (!a.kind_source || a.kind_source === "manual") return a.kind_source === "manual" ? "manuell" : null;
  const pct = a.kind_confidence != null ? Math.round(a.kind_confidence * 100) : null;
  return `${a.kind_source === "ai" ? "KI" : "erkannt"}${pct != null ? ` ${pct} %` : ""}`;
};

const LabelIcon = ({ label }) => {
  if (label === "Zahlungsbeleg") return <CreditCard className="h-3.5 w-3.5 text-emerald-600" />;
  if (label === "Lieferschein") return <Truck className="h-3.5 w-3.5 text-sky-600" />;
  if (label === "Mahnung") return <Bell className="h-3.5 w-3.5 text-amber-600" />;
  if (label === "AGB") return <ScrollText className="h-3.5 w-3.5 text-slate-500" />;
  if (label === "Vertrag") return <FileSignature className="h-3.5 w-3.5 text-slate-500" />;
  if (label === "Offerte") return <ClipboardList className="h-3.5 w-3.5 text-slate-500" />;
  return <FileText className="h-3.5 w-3.5 text-slate-500" />;
};

export const DOC_PATH = { bill: "bills", receipt: "receipts" };
export const DOC_LABEL = { bill: "Eingangsrechnung", receipt: "Beleg" };

const filePath = (docType, id, attachmentId) => {
  if (!attachmentId) return `/${DOC_PATH[docType]}/${id}/file`;
  return docType === "bill" ? `/bills/${id}/file?attachment=${attachmentId}` : `/receipts/${id}/attachments/${attachmentId}/file`;
};

export function useBillPreview(docType = "bill") {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(null);
  const base = DOC_PATH[docType];

  const open = async (bill, attachmentId = null) => {
    if (attachmentId === "delivery") {
      setPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url: null, mime: "", bill, activeId: "delivery" };
      });
      return;
    }
    setLoading(bill._id);
    try {
      const r = await apiClient.get(filePath(docType, bill._id, attachmentId), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url, mime: r.data.type || "", bill, activeId: attachmentId };
      });
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Keine Datei zu dieser Eingangsrechnung hinterlegt" : "Dokument konnte nicht geladen werden");
    } finally {
      setLoading(null);
    }
  };

  const close = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const updateBill = (updated) => setPreview((p) => (p ? { ...p, bill: updated } : p));

  const removeAttachment = async (bill, fileId, onUpdated) => {
    const att = (bill.attachments || []).find((a) => a.file_id === fileId);
    if (!window.confirm(`Beilage «${att?.file_name || ""}» wirklich entfernen?`)) return;
    try {
      await apiClient.delete(`/${base}/${bill._id}/attachments/${fileId}`);
      const updated = { ...bill, attachments: (bill.attachments || []).filter((a) => a.file_id !== fileId) };
      if (bill.payment_receipt_file_id === fileId) {
        updated.payment_receipt_file_id = null;
        updated.charged_amount_chf = null;
      }
      onUpdated(updated);
      toast.success("Beilage entfernt");
      if (preview?.activeId === fileId) await open(updated, null);
      else updateBill(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Beilage konnte nicht entfernt werden");
    }
  };

  const setKind = async (bill, fileId, kind, onUpdated) => {
    try {
      const r = await apiClient.put(`/${base}/${bill._id}/attachments/${fileId}/kind`, { kind });
      onUpdated(r.data.doc);
      updateBill(r.data.doc);
      toast.success(`Beilage als «${KIND_LABELS[kind]}» markiert${r.data.delivery_check ? ` · ${r.data.delivery_check.message}` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Art konnte nicht geändert werden");
    }
  };

  const classify = async (bill, onUpdated) => {
    try {
      const r = await apiClient.post(`/${base}/${bill._id}/attachments/classify?force=true`);
      onUpdated(r.data.doc);
      updateBill(r.data.doc);
      toast.success("Beilagen klassifiziert" + (r.data.delivery_check ? ` · ${r.data.delivery_check.message}` : ""));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Klassifizierung fehlgeschlagen");
    }
  };

  return { preview, loading, open, close, removeAttachment, setKind, classify, updateBill, docType };
}

export function BillRowActions({ bill, onOpen, loading, onAddAttachment, docType = "bill" }) {
  const atts = bill.attachments || [];
  const isBill = docType === "bill";
  return (
    <span className="inline-flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-1.5 text-slate-600 hover:text-violet-700"
        onClick={() => onOpen(bill, null)}
        disabled={loading === bill._id || (!bill.file_path && !bill.file_id && !bill.file_name)}
        title={(!bill.file_path && !bill.file_id && !bill.file_name) ? "Keine Datei hinterlegt" : isBill ? "Rechnung anzeigen" : "Beleg anzeigen"}
        data-testid={`bill-preview-button-${bill._id}`}
      >
        {loading === bill._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
      </Button>
      {atts.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 rounded-full border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] text-slate-700 hover:bg-violet-50 hover:border-violet-300"
              title={`${atts.length} Beilage${atts.length > 1 ? "n" : ""}`}
              data-testid={`bill-attachments-button-${bill._id}`}
            >
              <Paperclip className="h-3.5 w-3.5" />
              {atts.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[260px]" data-testid={`bill-attachments-menu-${bill._id}`}>
            <DropdownMenuLabel className="text-xs text-slate-500">Dokumente</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onOpen(bill, null)} data-testid={`bill-attachment-main-${bill._id}`}>
              <FileText className="h-3.5 w-3.5 mr-2 text-violet-600" />
              <span className="flex flex-col">
                <span className="text-xs font-medium">{isBill ? "Rechnung" : "Beleg"} (Hauptdokument)</span>
                {(bill.file_name || bill.original_filename) && <span className="text-[10px] text-slate-500 truncate max-w-[220px]">{bill.file_name || bill.original_filename}</span>}
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {atts.map((a) => {
              const label = attachmentLabel(a, bill);
              const conf = confidenceText(a);
              return (
                <DropdownMenuItem key={a.file_id} onClick={() => onOpen(bill, a.file_id)} data-testid={`bill-attachment-item-${a.file_id}`}>
                  <span className="mr-2"><LabelIcon label={label} /></span>
                  <span className="flex flex-col">
                    <span className="text-xs font-medium">
                      {label}
                      {conf && <span className="ml-1 text-[10px] font-normal text-slate-400">· {conf}</span>}
                    </span>
                    <span className="text-[10px] text-slate-500 truncate max-w-[220px]">{a.file_name}</span>
                  </span>
                </DropdownMenuItem>
              );
            })}
            {isBill && (bill.delivery_check || atts.some((a) => a.kind === "delivery_note")) && (
              <DropdownMenuItem onClick={() => onOpen(bill, "delivery")} data-testid={`bill-attachment-delivery-${bill._id}`}>
                <Truck className="h-3.5 w-3.5 mr-2 text-sky-600" />
                <span className="text-xs font-medium">Lieferschein-Abgleich</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAddAttachment(bill)} data-testid={`bill-attachment-add-${bill._id}`}>
              <Plus className="h-3.5 w-3.5 mr-2 text-violet-600" />
              <span className="text-xs font-medium">Beilage hochladen</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          type="button"
          onClick={() => onAddAttachment(bill)}
          className="inline-flex items-center rounded-full border border-dashed border-slate-300 bg-white px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700"
          title="Beilage nachreichen (Zahlungsbeleg, Lieferschein …)"
          data-testid={`bill-attachment-add-${bill._id}`}
        >
          <Paperclip className="h-3.5 w-3.5" />
          <Plus className="h-3 w-3 -ml-0.5" />
        </button>
      )}
      {isBill && <DeliveryCheckBadge bill={bill} onClick={() => onOpen(bill, "delivery")} />}
    </span>
  );
}

export function BillPreviewDialog({ preview, onClose, onSwitch, onRemove, onAddAttachment, onSetKind, onClassify, onBillUpdated, docType = "bill" }) {
  const bill = preview?.bill;
  const isBill = docType === "bill";
  const atts = bill?.attachments || [];
  const isDelivery = preview?.activeId === "delivery";
  const active = atts.find((a) => a.file_id === preview?.activeId);
  const showDeliveryTab = isBill && bill && (bill.delivery_check || atts.some((a) => a.kind === "delivery_note"));
  const tabCls = (isActive) =>
    `rounded-full px-3 py-1 text-xs border ${isActive ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`;
  return (
    <Dialog open={!!preview} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="bill-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {DOC_LABEL[docType]} {isBill ? bill?.bill_number || "" : bill?.description || ""}
            {active && ` · ${attachmentLabel(active, bill)}`}
            {isDelivery && " · Lieferschein-Abgleich"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="bill-preview-tabs">
          <button className={tabCls(!preview?.activeId)} onClick={() => onSwitch(bill, null)} data-testid="bill-preview-tab-main">
            {isBill ? "Rechnung" : "Beleg"}
          </button>
          {atts.map((a) => (
            <button
              key={a.file_id}
              className={tabCls(preview.activeId === a.file_id)}
              onClick={() => onSwitch(bill, a.file_id)}
              data-testid={`bill-preview-tab-${a.file_id}`}
            >
              {attachmentLabel(a, bill)} · {a.file_name}
            </button>
          ))}
          {showDeliveryTab && (
            <button className={`${tabCls(isDelivery)} inline-flex items-center gap-1`} onClick={() => onSwitch(bill, "delivery")} data-testid="bill-preview-tab-delivery">
              <Truck className="h-3 w-3" /> Lieferschein-Abgleich
            </button>
          )}
          <button
            type="button"
            className="rounded-full px-2.5 py-1 text-xs border border-dashed border-slate-300 text-slate-600 hover:bg-violet-50 hover:border-violet-300"
            onClick={() => onAddAttachment(bill)}
            data-testid="bill-preview-add-attachment"
          >
            <Plus className="inline h-3 w-3 mr-0.5" />Beilage
          </button>
          {atts.length > 0 && (
            <button
              type="button"
              className="rounded-full px-2.5 py-1 text-xs text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1"
              onClick={() => onClassify(bill)}
              title="Beilagen-Art per KI (neu) erkennen"
              data-testid="bill-preview-classify"
            >
              <Sparkles className="h-3 w-3" /> KI-Klassifizierung
            </button>
          )}
          {active && (
            <span className="ml-auto inline-flex items-center gap-2">
              <label className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                Art:
                <select
                  className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11px]"
                  value={active.kind && KIND_LABELS[active.kind] ? active.kind : "other"}
                  onChange={(e) => onSetKind(bill, active.file_id, e.target.value)}
                  data-testid={`bill-attachment-kind-select-${active.file_id}`}
                >
                  {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              {confidenceText(active) && <span className="text-[10px] text-slate-400" title={active.kind_reason || ""}>{confidenceText(active)}</span>}
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs text-rose-600 hover:bg-rose-50"
                onClick={() => onRemove(bill, active.file_id)}
                title="Diese Beilage entfernen"
                data-testid={`bill-preview-remove-${active.file_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Entfernen
              </button>
            </span>
          )}
        </div>
        {active?.kind_reason && (
          <p className="text-[11px] text-slate-500 -mt-1" data-testid="bill-attachment-kind-reason">{active.kind_reason}</p>
        )}
        {isDelivery && bill && <DeliveryCheckPanel bill={bill} onUpdated={onBillUpdated} />}
        {!isDelivery && preview?.url && (
          <div className="h-[70vh] rounded border border-slate-200 overflow-hidden flex bg-slate-100">
            <DocumentViewer blobUrl={preview.url} contentType={preview.mime} fileName={active?.file_name || "Dokument"} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
